import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Form, Input, List, Button, notification, ConfigProvider, Empty, Modal, Table } from 'antd';
import axios from 'axios';
import * as _ from 'lodash';
import { Icon } from 'dlweb-components';

const NewADUser = ({hideModal, onSuccess, adminSettings}) => {

  const pageSize = 25;

  const paginationSettings = {
    total: 0,
    defaultCurrent: 1,
    defaultPageSize: pageSize
  }

  const [form] = Form.useForm();

  const inputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [starting, setStarting] = useState(false);
  const [pagination, setPagination] = useState(paginationSettings);
  const [domainInputValue, setDomainInputValue] = useState('');
  const [searchDisabled, setSearchDisabled] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); 

  const formItemLayout = {
    labelCol: {
      xs: { span: 6 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 12 },
      sm: { span: 12 },
    },
  };

  useEffect(() => {
    const { activeDirectory } = adminSettings;
    const { domain } = activeDirectory;

    if(domain) {
      form.setFieldsValue({domain});
      setDomainInputValue(domain);
    }
  }, [form, adminSettings, setSearchDisabled])

  const getUsers = (postData) => {

    const pd = postData || {
      skip: 0,
      limit: pageSize,
    }

    setLoading(true);

    const {username, domain} = form.getFieldsValue();

    let userFilter = { __domain: domain };
    
    if(username?.trim().length > 0) {
      userFilter.samAccountName = `${username}*`;
    }

    pd.adUserFilter = userFilter;

    pd.order = [{
      column: 'samAccountName',
      direction: 0
    }],

    axios.post('/api/activedirectory/search/dto/wt/userman/grid', pd).then((res) => {
      setLoading(false);
      setUsers(res.data.data)
      setPagination(p => {
        let pSettings = {
          ...p,
          total: res.data._meta.totalRecords
        }

        if(pd.skip === 0) pSettings.current = 1;

        return pSettings;
      });
    })
    .catch(error => {
      console.error(error);
      alert('Error getting users!');
    })
  }

  const handleSubmit = values => {

    setStarting(true);
    setDisabled(true);

    const { domain } = values;

    const postData = {
      ids: selectedRowKeys,
      domain: domain
    }

    axios.post('/api/users/create/dto/userman/multiple/activedirectory', postData).then((res) => {
      if(res.data.errors.length > 0) {
        Modal.error({
          icon: null,
          title: <>
                    <Icon type='times-circle' size="lg" style={{color: 'var(--error-color)', marginRight: '10px'}}/>
                    Error
                 </>,
          content: <div>
                      <ul style={{listStyle: 'none', margin: '20px 0', padding: 0, maxHeight: '200px', overflowY: 'auto'}}>
                        {res.data.errors.map((err, i) => <div key={i}>{err}</div>)}
                      </ul>
                    </div>
        });

        setStarting(false);
        setDisabled(true);
        setSelectedRowKeys([]);
        onSuccess(); // calling this to refresh main user grid in case some users actually got saved before the error
        getUsers();
      }
      else {
        setStarting(false);
        setDisabled(false);
        hideModal();
        onSuccess();
        notification.success({ message: `User${users.length > 1 ? 's' : ''} created successfully!` });
      }
    })
    .catch(error => {
      setStarting(false);
      setDisabled(false);
      setSelectedRowKeys([]);
      console.error(error);
      alert('Error creating users!');
    })
  }

  const preventInvalidChars = (e) => {
    // allow alphanumeric, backspace, _, ', ., *
    if (e.key.match(/[^A-Za-z0-9\x08\_\'\.\*]/)) {
      e.preventDefault();
    }
  }

  const addUser = (e) => {
    
    e.preventDefault();

    if(!domainInputValue || isDuplicate()) { return }

    setDomainInputValue('');
    setUsers([...users, domainInputValue ]);

    inputRef.current.focus();
  }

  const columns = [
    {
      title: 'Username',
      width: '50%',
      dataIndex: 'samAccountName',
      ellipsis: true
    },
    {
      title: 'Full Name',
      width: '50%',
      ellipsis: true,
      render: (text, record) => <>{record.fullName}</>
    }
  ];

  useEffect(() => {
    if(selectedRowKeys.length > 0) setDisabled(false);
  }, [selectedRowKeys])

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedItems) => {
      setSelectedRowKeys(selectedItems);
    },
    getCheckboxProps: (record) => ({
      disabled: record.exists,
      name: record.samAccountName
    }),
  };

  useEffect(() => {
    setSearchDisabled(domainInputValue.trim().length > 0 ? false : true)
  }, [domainInputValue])

  const handleTableChange = (pagination) => {

    const pager = { ...pagination };
    pager.current = pagination.current;
    setPagination(pager);

    const cf = {
      limit: pagination.pageSize,
      skip: (pagination.current - 1) * pagination.pageSize,
    }

    getUsers(cf);
  };

  return (
    <Container>
      <Form
        form={form}
        {...formItemLayout}
        onFinish={handleSubmit}
        labelAlign="left"
        onValuesChange={() => {}}
      >
        <div style={{padding: '10px', verticalAlign: 'top'}}>
          <Form.Item label="Domain name" name="domain" style={{marginBottom: '10px'}}>
              <Input
                autoFocus
                ref={inputRef}
                onPressEnter={addUser}
                onKeyDown={preventInvalidChars}
                onChange={(e) => { setDomainInputValue(e.target.value)} }
              />
          </Form.Item>

          <Form.Item label="Username filter (optional)" name="username" style={{marginBottom: '20px'}}>
              <Input
                onKeyDown={preventInvalidChars}
                maxLength={50}
              />
          </Form.Item>

          <Button
              type="primary"
              style={{ position: 'absolute', marginTop: '10px', right: '35px', top: '122px' }}
              loading={starting}
              disabled={searchDisabled}  
              onClick={() => getUsers()}
            >
              Search for users
            </Button>

          <Form.Item
            label="Add users to Document Locator"
            wrapperCol={{
              xs: { span: 24 },
              sm: { span: 24 },
            }}
            labelCol={{
              xs: { span: 24 },
              sm: { span: 24 },
            }}
            style={{marginBottom: '-20px'}}
          >
            <ConfigProvider renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Users"/>}>
              <Table
                size="small"
                scroll={{scrollToFirstRowOnChange: true, y: '400px'}}
                loading={loading}
                pagination={{...pagination}}
                rowSelection={{
                  type: 'checkbox',
                  ...rowSelection,
                }}
                columns={columns}
                dataSource={users}
                rowKey="samAccountName"
                onChange={handleTableChange}
                rowClassName={record => record.exists && "disabled-row"}
                // onRow={(record, rowIndex) => {
                //   return {
                //     onClick: event => onRowClick(event, record)
                //   };
                // }}
              />
            </ConfigProvider>
          </Form.Item>
        </div>
        <Form.Item
          wrapperCol={{
            xs: { span: 24 },
            sm: { span: 24 },
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            style={{ float: 'right', margin: '0 10px' }}
            loading={starting}
            disabled={disabled}  
          >
            Create User{users.length > 1 && 's'}
          </Button>
          <Button
            style={{ float: 'right' }}
            onClick={hideModal}
            >
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-pagination {
    float: left;
  }
  .ant-table-body,
  .ant-table-placeholder {
    height: 400px;
  },
  .ant-checkbox-wrapper-disabled {

  }
  .disabled-row {
    position: relative;
    background: #f9f9fa;
    color: #ccc;
    .ant-table-selection-column::after {
      position: absolute;
      top: ${p => window.dl.isElectron ? '0' : '7px'};
      margin-top: ${p => window.dl.isElectron ? '7px' : '0'};
      left: 10px;
      width: 35px;
      height: 25px;
      border-radius: 2px;
      display: block;
      background: #2269a4 url(/images/logo/logo.png) 45% 40% no-repeat;
      background-size: auto 15px;
      content : " ";
    }
    &:hover > td {
      background: #f9f9fa !important;
    }
  }
`

export default NewADUser;